// src/hooks/useHelpDismissal.ts
// Per-panel help dismissal cache with content-version semantics.
//
// Storage key per mode: `style.helpDismissed.v1.<panelId>` -> last dismissed
// content version (number). 0 means "never dismissed".
//
// Auto-open rule: when the current MODE_HELP_VERSION[panelId] is greater than
// the cached dismissed version, the help popover should auto-open. Clicking
// "Don't show me this again" stamps the current version into the cache so the
// popover stays closed until the content is updated (and its version bumped).
//
// Dual-storage rationale (dev-sync note 268a2540 follow-up, 2026-06-15):
// the original implementation routed the dismiss-time write through
// `useLocalStorageState`, which calls `localStorage.setItem` from inside a
// React state-updater callback. On mobile Chrome a small fraction of those
// writes were observed to not survive a same-tab page reload — the popover
// auto-opened again on next visit even though the in-session dismiss had
// "stuck". The exact mechanism (write batched after navigation, eviction
// pressure, etc.) isn't fully pinned down. The fix is layered:
//
//   1. Write to localStorage *synchronously* at click time, outside any
//      React state-updater. The write completes before the React batch
//      processes the open/close cascade.
//   2. Also write to sessionStorage as a same-tab fallback. If localStorage
//      eviction ever drops the value between sessions, sessionStorage still
//      survives same-tab F5 / pull-to-refresh reloads.
//   3. On init read both stores and take the max — whichever has the
//      higher dismissed version wins, so a stale localStorage value can't
//      out-vote a fresh sessionStorage dismissal.
//
// Migration: the old single-toggle keys (`bb-mode-help-open`,
// `platformGlossaryOpen`) are NOT migrated. Old keys become orphan and harmless.
import { useCallback, useEffect, useState } from "react";
import { getModeHelpVersion } from "@/components/shell/help/modeHelpVersions";
import type { PrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import { BB_UI_PREFS_HYDRATED_EVENT, patchUserUiPrefs } from "@/lib/userUiPrefs";

const KEY_PREFIX = "style.helpDismissed.v1.";

/** Storage-key constructor exported for testing + future cross-file consumers. */
export function helpDismissalStorageKey(panelId: PrimaryMode): string {
  return `${KEY_PREFIX}${panelId}`;
}

function parseStoredVersion(raw: string | null): number {
  if (raw === null) return 0;
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed === "number" && Number.isFinite(parsed) && parsed >= 0) {
      return parsed;
    }
    return 0;
  } catch {
    return 0;
  }
}

/** Read the dismissed version for a key from both stores; returns the higher. */
export function readDismissedVersionFromStorage(storageKey: string): number {
  if (typeof window === "undefined") return 0;
  let max = 0;
  try {
    max = Math.max(max, parseStoredVersion(window.localStorage.getItem(storageKey)));
  } catch {
    // Private mode / disabled storage. Fall through to sessionStorage.
  }
  try {
    max = Math.max(max, parseStoredVersion(window.sessionStorage.getItem(storageKey)));
  } catch {
    // Same handling — silent fallback.
  }
  return max;
}

/** Write the dismissed version to both stores. Best-effort; failures are silent. */
export function writeDismissedVersionToStorage(storageKey: string, version: number): void {
  if (typeof window === "undefined") return;
  const payload = JSON.stringify(version);
  try {
    window.localStorage.setItem(storageKey, payload);
  } catch {
    // Private mode / quota. The sessionStorage path below still tries.
  }
  try {
    window.sessionStorage.setItem(storageKey, payload);
  } catch {
    // Same handling.
  }
}

/** Clear the dismissed version from both stores. */
export function clearDismissedVersionInStorage(storageKey: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(storageKey);
  } catch {
    // ignore
  }
  try {
    window.sessionStorage.removeItem(storageKey);
  } catch {
    // ignore
  }
}

/** Session-only stamp so ✕ stops auto-open in this tab without "Don't show again". */
export function writeDismissedVersionToSessionStorage(storageKey: string, version: number): void {
  if (typeof window === "undefined") return;
  const payload = JSON.stringify(version);
  try {
    window.sessionStorage.setItem(storageKey, payload);
  } catch {
    // Private mode / quota.
  }
}

export type UseHelpDismissalResult = {
  dismissedVersion: number;
  currentVersion: number;
  shouldAutoOpen: boolean;
  dismiss: () => void;
  dismissForSession: () => void;
  resetForTesting: () => void;
};

export function useHelpDismissal(panelId: PrimaryMode): UseHelpDismissalResult {
  const storageKey = helpDismissalStorageKey(panelId);
  const [dismissedVersion, setDismissedVersion] = useState<number>(() =>
    readDismissedVersionFromStorage(storageKey)
  );

  const currentVersion = getModeHelpVersion(panelId);
  const shouldAutoOpen = dismissedVersion < currentVersion;

  useEffect(() => {
    setDismissedVersion(readDismissedVersionFromStorage(storageKey));
  }, [storageKey]);

  const dismiss = useCallback(() => {
    // Synchronous storage write FIRST. This happens before React processes
    // any subsequent setState batch (popover close, drawer close), so the
    // value is durable even if React drops the in-component update because
    // the popover panel unmounts in the same render.
    writeDismissedVersionToStorage(storageKey, currentVersion);
    setDismissedVersion(currentVersion);
    void patchUserUiPrefs({ helpDismissed: { [panelId]: currentVersion } });
  }, [storageKey, currentVersion, panelId]);

  const dismissForSession = useCallback(() => {
    writeDismissedVersionToSessionStorage(storageKey, currentVersion);
    setDismissedVersion((prev) => Math.max(prev, currentVersion));
  }, [storageKey, currentVersion]);

  useEffect(() => {
    const onHydrated = () => {
      setDismissedVersion(readDismissedVersionFromStorage(storageKey));
    };
    window.addEventListener(BB_UI_PREFS_HYDRATED_EVENT, onHydrated);
    return () => window.removeEventListener(BB_UI_PREFS_HYDRATED_EVENT, onHydrated);
  }, [storageKey]);

  const resetForTesting = useCallback(() => {
    clearDismissedVersionInStorage(storageKey);
    setDismissedVersion(0);
  }, [storageKey]);

  return {
    dismissedVersion,
    currentVersion,
    shouldAutoOpen,
    dismiss,
    dismissForSession,
    resetForTesting,
  };
}
